import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from '../../axios/axiosconf';
import { getFormaPagoTexto } from '../../../utils/formasPago';

function VistaPreviaFactura() {
    const { id } = useParams();
    const [factura, setFactura] = useState(null);
    const [client, setClient] = useState(null);
    const [paciente, setPaciente] = useState(null);
    const [loading, setLoading] = useState(true);
    const [encabezadoPersonalizado, setEncabezadoPersonalizado] = useState(null); // 🆕 Estado para encabezado personalizado
    const [respuestaDian, setRespuestaDian] = useState(null); // 🆕 Estado para respuesta DIAN
    const [facturador, setFacturador] = useState(null); // 🆕 Estado para el usuario que creó la factura

    useEffect(() => {
        cargarDatos();
    }, [id]);

    const getMetodoPagoTexto = (codigo) => getFormaPagoTexto(codigo);

    const cargarRespuestaDian = async (facturaId) => {
        try {
            const response = await axios.get(`/factura/${facturaId}/respuesta-dian`);
            if (response.data.success) {
                setRespuestaDian(response.data.data);
            }
        } catch (error) {
            console.log('No hay respuesta DIAN para esta factura');
        }
    };

    const cargarDatos = async () => {
        let userdata = JSON.parse(localStorage.getItem('userdata') || '{}');

        const config = {
            headers: {
                Authorization: 'Bearer ' + userdata.access_token
            }
        };

        try {
            const [clienteResponse, facturaResponse, pacienteResponse] = await Promise.all([
                axios.get('/get-client?uid=' + userdata.user_id, config),
                axios.get(`/factura/${id}?uid=${userdata.user_id}`, config),
                axios.get(`/pacientes?uid=${userdata.user_id}`, config)
            ]);

            setClient(clienteResponse.data);
            setFactura(facturaResponse.data);

            let pacienteEncontrado = pacienteResponse.data.find(
                (p) => String(p.id) === String(facturaResponse.data.paciente_id)
            );
            if (!pacienteEncontrado?.numero_documento && facturaResponse.data.paciente_id) {
                try {
                    const pacienteDetalle = await axios.get(
                        `/paciente?uid=${userdata.user_id}&id=${facturaResponse.data.paciente_id}`,
                        config
                    );
                    if (pacienteDetalle.data?.[0]) {
                        pacienteEncontrado = pacienteDetalle.data[0];
                    }
                } catch (error) {
                    console.log('No se pudo cargar el detalle del paciente:', error);
                }
            }
            setPaciente(pacienteEncontrado);

            // 🆕 Cargar encabezado personalizado del usuario que creó la factura
            if (facturaResponse.data.users_id) {
                try {
                    const encabezadoResponse = await axios.get(`/encabezado-personalizado?uid=${facturaResponse.data.users_id}`, config);
                    if (encabezadoResponse.status === 200 && encabezadoResponse.data && encabezadoResponse.data.estado === 1) {
                        setEncabezadoPersonalizado(encabezadoResponse.data);
                        console.log('Encabezado personalizado cargado:', encabezadoResponse.data);
                    }
                } catch (error) {
                    console.log('No hay encabezado personalizado habilitado:', error);
                    setEncabezadoPersonalizado(null);
                }

                // 🆕 Cargar información del usuario que creó la factura (facturador)
                try {
                    const usuariosResponse = await axios.get(`/users-by-client?uid=${userdata.user_id}`, config);
                    const usuarioEncontrado = usuariosResponse.data.find(u => u.id == facturaResponse.data.users_id);
                    if (usuarioEncontrado) {
                        setFacturador(usuarioEncontrado);
                        console.log('Facturador cargado:', usuarioEncontrado);
                    }
                } catch (error) {
                    console.log('No se pudo cargar el facturador:', error);
                }
            }

            // 🆕 Cargar respuesta DIAN para obtener CUFE
            await cargarRespuestaDian(id);

        } catch (error) {
            console.error('Error al cargar datos:', error);
            alert('Error al cargar los datos de la factura');
        } finally {
            setLoading(false);
        }
    };

    const imprimir = () => {
        window.print();
    };

    const enviarWhatsApp = async () => {
        const telefonoPaciente = paciente.celular || paciente.telefono;
        if (!telefonoPaciente) {
            alert('El paciente no tiene teléfono registrado');
            return;
        }

        const numeroWhatsApp = telefonoPaciente.startsWith('+57') ? telefonoPaciente : `+57${telefonoPaciente}`;
        const mensaje = `Hola ${nombrePaciente}, te enviamos tu factura N° ${factura.numero_factura} por valor de $${Math.round(parseFloat(factura.valor_total)).toLocaleString('es-CO')}. Fecha de vencimiento: ${new Date(factura.fecha_vence).toLocaleDateString()}.`;

        try {
            // Encode message for URL
            const url = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`;
            window.open(url, '_blank');

            // alert('Mensaje enviado por WhatsApp exitosamente'); // No needed as window opens
        } catch (error) {
            console.error('Error enviando WhatsApp:', error);
            alert('Error al abrir WhatsApp');
        }
    };

    if (loading || !factura || !client || !paciente) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                    <p className="mt-2">Cargando factura...</p>
                </div>
            </div>
        );
    }

    const nombrePaciente = `${paciente.primer_nombre || ''} ${paciente.segundo_nombre || ''} ${paciente.primer_apellido || ''} ${paciente.segundo_apellido || ''}`.trim();

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        const dia = String(date.getDate()).padStart(2, '0');
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const anio = String(date.getFullYear()).slice(-2);
        return `${dia}/${mes}/${anio}`;
    };

    const construirDireccionCompleta = () => {
        const partes = [];
        if (paciente.direccion) partes.push(paciente.direccion);
        if (paciente.zona) partes.push(paciente.zona);
        if (paciente.nombre_ciudad_residencia) partes.push(paciente.nombre_ciudad_residencia);
        if (paciente.nombre_departamento_residencia) partes.push(paciente.nombre_departamento_residencia);
        if (paciente.nombre_pais_residencia) partes.push(paciente.nombre_pais_residencia);
        return partes.join(', ') || paciente.direccion || '';
    };

    const mostrarSectorSaludFactura = () => {
        if (!factura) return false;
        const poliza = String(factura.policy_number ?? '').trim();
        const contrato = String(factura.contract_number ?? '').trim();
        return poliza !== '' || contrato !== '';
    };

    const valorPolizaFactura = () => String(factura?.policy_number ?? '').trim();
    const valorContratoFactura = () => String(factura?.contract_number ?? '').trim();

    const obtenerNit = () => {
        if (!paciente) return '';
        return paciente.numero_documento || paciente.numeroDocumento || '';
    };

    const mostrarDescripcion = factura.detalle?.some(item => String(item.descripcion || '').trim() !== '');
    const colspanTotales = mostrarDescripcion ? 7 : 6;

    // 🆕 Función para generar encabezado personalizado HTML
    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) {
            return null;
        }

        return (
            <div style={{ marginBottom: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '30%', padding: '2px', fontSize: '10px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.izquierda }} />
                            <td style={{ width: '40%', padding: '2px', textAlign: 'center', fontSize: '11px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.centro }} />
                            <td style={{ width: '30%', padding: '2px', textAlign: 'right', fontSize: '10px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.derecha }} />
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="container-fluid p-0">
            <div className="invoice p-2" id="invoice-content">
                {/* 🆕 Encabezado Personalizado */}
                {generarEncabezadoHTML()}

                {/* 🆕 BANNER: FACTURA DE VENTA */}
                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <div style={{
                            backgroundColor: '#e9ecef',
                            padding: '4px 8px',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            borderRadius: '3px',
                            textAlign: 'center',
                            border: '1px solid #dee2e6'
                        }}>
                            FACTURA DE VENTA: {factura.prefijo || ''}{factura.prefijo ? ' ' : ''}{factura.numero_factura}
                        </div>
                    </div>
                </div>

                {/* 🆕 Datos de la factura */}
                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <table className="table table-bordered w-100 mb-0" style={{ fontSize: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>FACTURA #</td>
                                    <td className="bg-white p-1" style={{ width: '18%' }}>{factura.numero_factura}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>PREFIJO</td>
                                    <td className="bg-white p-1" style={{ width: '18%' }}>{factura.prefijo || 'N/A'}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>CREACIÓN</td>
                                    <td className="bg-white p-1" style={{ width: '19%' }}>{formatearFecha(factura.fecha_emision)}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">FORMA DE PAGO</td>
                                    <td className="bg-white p-1">{getMetodoPagoTexto(factura.forma_pago)}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">FACTURADOR</td>
                                    <td className="bg-white p-1">{facturador ? facturador.name : 'N/A'}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">VENCIMIENTO</td>
                                    <td className="bg-white p-1">{formatearFecha(factura.fecha_vence)}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">CONCEPTO</td>
                                    <td colSpan="5" className="bg-white p-1">
                                        {factura.concepto_factura_nombre || '—'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <table className="table table-bordered w-100 mb-0" style={{ fontSize: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>RAZÓN SOCIAL</td>
                                    <td className="bg-white p-1" style={{ width: '35%' }}>{nombrePaciente}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>NIT</td>
                                    <td className="bg-white p-1" style={{ width: '35%' }}>{obtenerNit()}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">TELÉFONO</td>
                                    <td className="bg-white p-1">{paciente.telefono || paciente.celular || ''}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">CELULAR</td>
                                    <td className="bg-white p-1">{paciente.celular || ''}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">EMAIL</td>
                                    <td className="bg-white p-1">{paciente.email || ''}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">DIRECCIÓN</td>
                                    <td className="bg-white p-1">{construirDireccionCompleta()}</td>
                                </tr>
                                {mostrarSectorSaludFactura() && (
                                    <tr>
                                        <td className="bg-light font-weight-bold align-middle p-1">N° PÓLIZA</td>
                                        <td className="bg-white p-1">{valorPolizaFactura()}</td>
                                        <td className="bg-light font-weight-bold align-middle p-1">N° CONTRATO</td>
                                        <td className="bg-white p-1">{valorContratoFactura()}</td>
                                    </tr>
                                )}
                                {/* CUFE para facturas electrónicas */}
                                {respuestaDian?.uuid && (
                                    <tr>
                                        <td className="bg-light font-weight-bold align-middle p-1">CUFE</td>
                                        <td colSpan="3" className="bg-white p-1 text-break" style={{ fontSize: '10px' }}>{respuestaDian.uuid}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {factura.presupuesto_id && factura.saldo_abono !== null && factura.saldo_abono !== undefined ? (
                    <div className="row m-0 mb-2">
                        <div className="col-12 p-0">
                            <div style={{
                                borderRadius: '6px',
                                border: '1px solid #bee5eb',
                                backgroundColor: '#d1ecf1',
                                color: '#0c5460',
                                padding: '6px 8px',
                                fontSize: '10px',
                                fontWeight: 600
                            }}>
                                Abono al Presupuesto #{factura.numero_presupuesto || factura.presupuesto_id}. Valor Total:{' '}
                                {(() => {
                                    const raw = factura.presupuesto_valor_total ?? factura.presupuestoValorTotal;
                                    const ok = raw !== null && raw !== undefined && raw !== '';
                                    return ok
                                        ? `$${Math.round(parseFloat(raw)).toLocaleString('es-CO')}`
                                        : 'N/D';
                                })()}
                                . Saldo Pendiente: $
                                {Math.round(parseFloat(factura.saldo_abono || 0)).toLocaleString('es-CO')}.
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="row m-0">
                    <div className="col-12 p-0 table-responsive">
                        <table className="table table-striped w-100 mb-0" style={{ fontSize: '10px' }}>
                            <thead>
                                <tr>
                                    <th className="p-1">Código</th>
                                    <th className="p-1">Servicio</th>
                                    {mostrarDescripcion && <th className="p-1">Descripción</th>}
                                    <th className="p-1">Cantidad</th>
                                    <th className="p-1">Precio Unit.</th>
                                    <th className="p-1">Descuento</th>
                                    <th className="p-1">Ajuste</th>
                                    <th className="p-1">Impuesto</th>
                                    <th className="text-right p-1">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {factura.detalle?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="p-1">{item.lista_precio?.codigo}</td>
                                        <td className="p-1">{item.servicio || item.lista_precio?.nombre}</td>
                                        {mostrarDescripcion && <td className="p-1">{item.descripcion || ''}</td>}
                                        <td className="p-1">{item.cantidad}</td>
                                        <td className="p-1">${Math.round(parseFloat(item.precio_unitario)).toLocaleString('es-CO')}</td>
                                        <td className="p-1">{item.porcentaje}%</td>
                                        <td className="p-1">${Math.round(parseFloat(item.ajuste) || 0).toLocaleString('es-CO')}</td>
                                        <td className="p-1">{item.impuesto}%</td>
                                        <td className="text-right p-1">${Math.round(parseFloat(item.total)).toLocaleString('es-CO')}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                    <td className="text-left font-weight-bold pl-2 p-1" style={{ borderTop: '2px solid #dee2e6' }}>Subtotal:</td>
                                    <td className="text-right p-1" style={{ borderTop: '2px solid #dee2e6' }}>${Math.round(parseFloat(factura.valor_bruto)).toLocaleString('es-CO')}</td>
                                </tr>
                                <tr>
                                    <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                    <td className="text-left font-weight-bold text-danger pl-2 p-1" style={{ border: 'none' }}>Descuento:</td>
                                    <td className="text-right text-danger p-1" style={{ border: 'none' }}>
                                        -${Math.round(factura.detalle?.reduce((acc, item) => {
                                            const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                            return acc + (precioBase * (parseFloat(item.porcentaje) / 100));
                                        }, 0) || 0).toLocaleString('es-CO')}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                    <td className="text-left font-weight-bold pl-2 p-1" style={{ border: 'none' }}>Ajuste:</td>
                                    <td className="text-right p-1" style={{ border: 'none' }}>
                                        ${Math.round(factura.detalle?.reduce((acc, item) => {
                                            return acc + (parseFloat(item.ajuste) || 0);
                                        }, 0) || 0).toLocaleString('es-CO')}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                    <td className="text-left font-weight-bold pl-2 p-1" style={{ border: 'none' }}>Impuesto:</td>
                                    <td className="text-right p-1" style={{ border: 'none' }}>
                                        ${Math.round(factura.detalle?.reduce((acc, item) => {
                                            const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                            const descuento = precioBase * (parseFloat(item.porcentaje) / 100);
                                            const baseAjustada = (precioBase - descuento) + (parseFloat(item.ajuste) || 0);
                                            return acc + (baseAjustada * (parseFloat(item.impuesto) / 100));
                                        }, 0) || 0).toLocaleString('es-CO')}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                    <td className="text-left font-weight-bold pl-2 p-1" style={{ borderTop: '1px solid #dee2e6', fontSize: '10px' }}>Total:</td>
                                    <td className="text-right font-weight-bold p-1" style={{ borderTop: '1px solid #dee2e6', fontSize: '10px' }}>${Math.round(parseFloat(factura.valor_total)).toLocaleString('es-CO')}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {factura.observaciones && (
                    <div className="row m-0 mt-2">
                        <div className="col-12 p-0">
                            <h6 style={{ fontSize: '10px', marginBottom: '2px', fontWeight: 'bold' }}>Observaciones:</h6>
                            <p style={{ fontSize: '10px', marginBottom: '0' }}>{factura.observaciones}</p>
                        </div>
                    </div>
                )}

                {/* Pie de página legal - Solo para facturas electrónicas */}
                {respuestaDian?.uuid && (
                    <div className="row m-0 mt-3" style={{ borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                        <div className="col-12 p-0">
                            <p style={{ fontSize: '8px', color: '#666', marginBottom: '0', textAlign: 'justify', lineHeight: '1.3' }}>
                                <strong>Nota Legal:</strong> Esta factura electrónica ha sido generada y transmitida conforme a lo establecido en el Decreto 2242 de 2015 y la Resolución DIAN 000042 de 2020. 
                                El CUFE (Código Único de Factura Electrónica) garantiza la autenticidad e integridad del documento. 
                                Para verificar la validez de este documento electrónico, puede consultar en el portal de la DIAN utilizando el CUFE proporcionado. 
                                Este documento tiene plena validez legal y probatoria de acuerdo con la normativa colombiana vigente.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body, html, #root {
                        height: auto;
                        margin: 0;
                        padding: 0;
                        background-color: white;
                    }
                    .container-fluid {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .invoice {
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    /* Evitar cortes de página en bloques */
                    .row {
                        page-break-inside: avoid;
                        margin: 0 !important;
                    }
                    table {
                        page-break-inside: auto;
                        width: 100% !important;
                    }
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    td, th {
                        padding: 2px !important; /* Reducir padding aún más para impresión */
                    }
                    @page {
                        size: landscape;
                        margin: 5mm; 
                    }
                }
            `}</style>
        </div>
    );
}

export default VistaPreviaFactura;